import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Info } from "lucide-react";

import { chatLists, convTitle, lastText, chatHref } from "@/lib/v3Messages";
import { initialsOf } from "@/lib/v3People";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { ResultTable, ResultRow, SectionTitle, V3_LINK } from "../components/v3app";
import { InfoBox } from "../components/v3people";
import { useConversations, ChatAside } from "../components/v3messages";

const requestsText = (n) => (n === 1 ? "1 offene Kontaktanfrage" : `${n} offene Kontaktanfragen`);

function BackButton() {
  const navigate = useNavigate();
  return <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} data-testid="messages-back" className={V3_SECONDARY}>Zurück</button>;
}

// M77: no confirmed chat and no circle chat yet.
function NoMessages({ incoming }) {
  return (
    <div data-m="M77" data-testid="messages-empty">
      <V3Title title="Noch keine Nachrichten" intro="Hier erscheinen Gespräche mit bestätigten Kontakten und aufgenommenen Kreisen." testid="messages-title" />
      <V3Columns layout="detail" aside={<ChatAside text="Noch kein Gespräch begonnen" />} main={<>
        <StatusBanner tone="sand" icon={Info} title="Noch kein Gespräch" text="Noch kein Gespräch begonnen" testid="messages-empty-banner" />
        <DetailList>
          <DetailRow label="Persönliche Kontakte" value="Noch kein Gespräch begonnen" testid="messages-empty-direct" />
          <DetailRow label="Kreisgespräche" value="Nach Aufnahme verfügbar" to="/kreise" testid="messages-empty-groups" />
          {incoming > 0 && <DetailRow label="Kontaktanfragen" value={requestsText(incoming)} to="/kontakte" testid="messages-empty-requests" />}
          <DetailRow label="Nachricht senden" value="Bestätigten Kontakt auswählen" to="/kontakte/bestaetigt" testid="messages-empty-send" />
          <DetailRow label="Hilfe" value="Kontaktanfragen verstehen" to="/hilfe" testid="messages-empty-help" />
        </DetailList>
        <V3Actions>
          <Link to="/kontakte" data-testid="messages-contacts" className={V3_PRIMARY}>Kontakte ansehen</Link>
          <BackButton />
        </V3Actions>
      </>} />
    </div>
  );
}

function ChatRows({ list, testid, label }) {
  return (
    <ResultTable testid={testid} label={label}>
      {list.map((c) => <ResultRow key={c.id} initial={initialsOf(convTitle(c))} title={convTitle(c)} context={lastText(c) || "Noch keine Nachricht"} to={chatHref(c)} testid={`chat-row-${c.id}`} />)}
    </ResultTable>
  );
}

// Overview of the confirmed direct chats and circle chats (GET /member/conversations).
function MessageList() {
  const res = useConversations();
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (!res.data) return <Loading />;
  const l = chatLists(res.data);
  if (!l.direct.length && !l.groups.length) return <NoMessages incoming={l.incoming} />;
  const n = l.direct.length + l.groups.length;
  return (
    <div data-m="M17" data-testid="messages-list">
      <V3Title title="Nachrichten" intro="Gespräche mit bestätigten Kontakten und aufgenommenen Kreisen." testid="messages-title" />
      <V3Columns layout="detail" aside={<ChatAside text={n === 1 ? "1 Gespräch" : `${n} Gespräche`} />} main={<>
        {l.incoming > 0 && <div data-testid="messages-requests">
          <InfoBox title={requestsText(l.incoming)} text="Direkte Nachrichten sind erst nach deiner Annahme möglich." />
          <Link to="/kontakte" data-testid="messages-requests-open" className={`${V3_LINK} mb-[6px]`}>Anfragen ansehen</Link>
        </div>}
        <section aria-labelledby="messages-direct-title" className="mt-[6px]">
          <SectionTitle id="messages-direct-title">Persönliche Kontakte</SectionTitle>
          {l.direct.length ? <ChatRows list={l.direct} testid="messages-direct" label="Persönliche Kontakte" />
            : <div className="mt-[11px]"><DetailRow label="Persönliche Kontakte" value="Noch kein Gespräch begonnen" to="/kontakte/bestaetigt" testid="messages-direct-empty" /></div>}
        </section>
        <section aria-labelledby="messages-groups-title" className="mt-[24px]">
          <SectionTitle id="messages-groups-title">Kreisgespräche</SectionTitle>
          {l.groups.length ? <ChatRows list={l.groups} testid="messages-groups" label="Kreisgespräche" />
            : <div className="mt-[11px]"><DetailRow label="Kreisgespräche" value="Nach Aufnahme verfügbar" to="/kreise" testid="messages-groups-empty" /></div>}
        </section>
        <V3Actions>
          <Link to="/kontakte" data-testid="messages-contacts" className={V3_PRIMARY}>Kontakte ansehen</Link>
          <BackButton />
        </V3Actions>
      </>} />
    </div>
  );
}

// Old links /nachrichten?gespraech=<id> lead to the conversation view.
export default function Messages() {
  const { search } = useLocation();
  const legacy = new URLSearchParams(search).get("gespraech");
  if (legacy) return <Navigate to={`/nachrichten/${encodeURIComponent(legacy)}`} replace />;
  return <MessageList />;
}
